import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// Services
import { getFooter, type FooterType } from '@/services/footer/footerService';
import { getHeader, type HeaderType } from '@/services/header/headerService';
import { getNewsPage, type NewsPageType } from '@/services/news-page/newsPageService';
import { getAllNews, type NewsType } from '@/services/news/newsService';
import type { MetaType } from '@/services/strapiSchema';

// Components
import Header from '@/components/organisms/Header/Header';
import Footer from '@/components/organisms/Footer/Footer';
import { PageBlocks } from '@/components/blocks/Blocks';
import PreTitle from '@/components/atoms/PreTitle/PreTitle';
import Heading from '@/components/atoms/Heading/Heading';
import BlocMiseEnAvant from '@/components/blocks/BlocMiseEnAvant/BlocMiseEnAvant';

// Utils
import { getLocale } from '@/utils/localeSlug';
import { buildSEOUrl } from '@/utils/seo';

// Styles
import styles from './News.module.css';

export async function newsPageLoader({ request }: { request: Request }) {
  const url = new URL(request.url);

  const locale = getLocale(url);

  const [header, footer, newsPage] = await Promise.all([
    getHeader(locale),
    getFooter(locale),
    getNewsPage(locale),
  ]);
  
  return {
    header,
    footer,
    newsPage,
    locale
  };
}

function News() {
  const baseUrl = import.meta.env.VITE_DOMAIN;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // States
  const [news, setNews] = useState<{ data: NewsType[]; meta: MetaType }>();
  const [newsError, setNewsError] = useState<Error | null>(null);

  // API Calls
  const data = useLoaderData() as Awaited<ReturnType<typeof newsPageLoader>>;

  if (!data) return null;
  const { header, footer, newsPage, locale } = data;

  // Pagination
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const totalPages = news?.meta.pagination.pageCount || 0;

  // Hooks
  useEffect(() => {
    if (currentPage < 1) {
      navigate('?page=1', { replace: true });
      return;
    }

    setNewsError(null);

    getAllNews(locale, currentPage)
      .then((newsData) => {
        const lastPage = newsData.meta.pagination.pageCount;

        if (lastPage > 0 && currentPage > lastPage) {
          navigate(`?page=${lastPage}`, { replace: true });
        } else {
          setNews(newsData);
        }
      })
      .catch((err) => {
        setNews(undefined);
        setNewsError(err instanceof Error ? err : new Error(String(err)));
      });
  }, [currentPage, locale, navigate]);


  // Handlers
  const handlePage = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      navigate(`?page=${newPage}`, { replace: true });
    }
  };

  if (!newsPage) return null;

  if (newsError) {
    return (
      <main>
        <Header color='dark' header={header as HeaderType} />
        <div className={styles.body}>
          <p role="alert">{newsError.message}</p>
        </div>
        <Footer footer={footer as FooterType} />
      </main>
    );
  }

  if (!news) {
    return (
      <main>
        <Header color='dark' header={header as HeaderType} />
        <div className={styles.body}>
          <p>{t('news-page.loading', { defaultValue: 'Chargement…' })}</p>
        </div>
        <Footer footer={footer as FooterType} />
      </main>
    );
  }

  return (
    <main>
      <Helmet htmlAttributes={{ lang: newsPage.locale }}>
        <title>{newsPage.seo.metaTitle}</title>
        <meta name="description" content={newsPage.seo.metaDescription} />
        <link 
          rel='canonical'
          href={newsPage.seo.canonicalLink ?? buildSEOUrl(baseUrl, newsPage.locale, newsPage.slug)}
        />
        <link
          rel='alternate' hrefLang={newsPage.locale}
          href={buildSEOUrl(baseUrl, newsPage.locale, newsPage.slug)}
        />
        {
          newsPage.localizations.map((localization) => (
            <link
              key={localization.documentId}
              rel="alternate"
              hrefLang={localization.locale}
              href={buildSEOUrl(baseUrl, localization.locale, localization?.slug)}
            />
          ))
        }
        {
          newsPage.locale === 'fr-FR' ? (
            <link
              rel="alternate"
              hrefLang="x-default"
              href={buildSEOUrl(baseUrl, newsPage.locale, newsPage.slug)}
            />
          ) : (
            (() => {
              const fr = newsPage.localizations.find(loc => loc.locale === 'fr-FR');
              if (fr) {
                return (
                  <link
                    rel="alternate"
                    hrefLang="x-default"
                    href={buildSEOUrl(baseUrl, fr.locale, fr.slug)}
                  />
                );
              }
              return null;
            })()
          )
        }
      </Helmet>
      
      <Header
        color='dark'
        header={header as HeaderType}
      />

      <div className={styles.body}>
        <PageBlocks blocks={(newsPage as NewsPageType).blocks} />
        <div className={styles.container}>
          <div className={styles['news-body']}>
            <div className={styles.content}>
              <PreTitle
                preTitle={newsPage.preTitle}
                color='dark'
              />
              <Heading
                title={newsPage.title}
                size='h2'
                Tag='h2'
                color='dark'
                align='center'
              />
            </div>
          </div>
          <div className={styles.cards}>
            {
              news.data.map((item) => (
                <BlocMiseEnAvant
                  key={item.documentId}
                  image={item.image}
                  title={item.title}
                  description={item.description}
                  url={item.url}
                  publicationDate={item.publicationDate}
                  imagePosition={item.imagePosition}
                />
              ))
            }
          </div>
          <div className={styles.pagination}>
            <button
              type='button'
              onClick={() => handlePage(currentPage - 1)}
              disabled={currentPage <= 1}
              className={styles['pagination-button']}
            >
              {t('news-page.previous')}
            </button>
            <div className={styles['pagination-pages']}>
              {
                currentPage > 1 ? (
                  <button
                    type='button'
                    onClick={() => handlePage(currentPage - 1)}
                    className={styles['pagination-button']}
                  >
                    {currentPage - 1}
                  </button>
                ) : null
              }
              <div className={styles['pagination-active']}>
                {currentPage}
              </div>
              {
                currentPage < totalPages ? (
                  <button
                    type='button'
                    onClick={() => handlePage(currentPage + 1)}
                    className={styles['pagination-button']}
                  >
                    {currentPage + 1}
                  </button>
                ) : null
              }
            </div>
            <button 
              type='button' 
              onClick={() => handlePage(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className={styles['pagination-button']}
            >
              {t('news-page.next')}
            </button>
          </div>
        </div>
      </div>
      <Footer
        footer={footer as FooterType}
      />
    </main>
  );
}

export default News;